import axios from 'axios';

// API_BASE: 
// - In development with React proxy (package.json "proxy"): use '/api'
// - In production (SPA served from .NET): use '/api'
// - Override via REACT_APP_API_URL env var if needed (e.g. "https://myserver:7120/api")
const API_BASE = process.env.REACT_APP_API_URL || '/api';

const api = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
  timeout: 30000, // 30 second timeout
});

// Request interceptor — attach JWT token
api.interceptors.request.use(
  (config) => {
    const token = sessionStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor — handle 401 (token expired)
api.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401) {
      // Only auto-logout if we had a token (not during login)
      const hadToken = !!sessionStorage.getItem('token');
      if (hadToken) {
        sessionStorage.removeItem('token');
        sessionStorage.removeItem('user');
        window.location.reload();
      }
    }
    return Promise.reject(err);
  }
);

// Xóa cờ auto-view file (TabFiles) — reset mỗi lần login/logout để phiên đăng nhập
// mới lại tự mở file kết quả như lần đầu
const clearAutoViewFlags = () => {
  Object.keys(sessionStorage)
    .filter(k => k.startsWith('autoview:'))
    .forEach(k => sessionStorage.removeItem(k));
};

// ========== AUTH ==========
export const AuthService = {
  login: async (DienThoai, MatKhau) => {
    const { data } = await api.post('/auth/login', { DienThoai, MatKhau });
    if (data.token) {
      clearAutoViewFlags();
      sessionStorage.setItem('token', data.token);
      sessionStorage.setItem('user', JSON.stringify(data.user));
    }
    return data;
  },

  // Đăng nhập qua SP web_xacthuc_nguoidung (endpoint mới, luồng login cũ giữ nguyên)
  loginNguoiDung: async (TenDangNhap, MatKhau) => {
    const { data } = await api.post('/auth/login-nguoidung', { DienThoai: TenDangNhap, MatKhau });
    if (data.token) {
      clearAutoViewFlags();
      sessionStorage.setItem('token', data.token);
      sessionStorage.setItem('user', JSON.stringify(data.user));
    }
    return data;
  },

  logout: () => {
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('user');
    clearAutoViewFlags();
  },

  getUser: () => {
    const raw = sessionStorage.getItem('user');
    return raw ? JSON.parse(raw) : null;
  },

  isLoggedIn: () => !!sessionStorage.getItem('token'),

  isAdmin: () => {
    const raw = sessionStorage.getItem('user');
    if (!raw) return false;
    try { return !!JSON.parse(raw).isAdmin; } catch { return false; }
  },

  changePassword: async (oldPassword, newPassword) => {
    const { data } = await api.post('/auth/change-password', { OldPassword: oldPassword, NewPassword: newPassword });
    return data;
  },
};

// ========== ADMIN / USERS ==========
export const UserAdminService = {
  list: async (search = '', page = 1, pageSize = 20) => {
    const { data } = await api.get('/users', { params: { search, page, pageSize } });
    return data;
  },

  resetPassword: async (dienThoai, newPassword) => {
    const { data } = await api.post('/users/reset-password', { DienThoai: dienThoai, NewPassword: newPassword });
    return data;
  },
};

// ========== VISITS (danh sách đợt KSK — KskResultsController) ==========
export const VisitService = {
  getVisits: async (idBenhNhan) => {
    const { data } = await api.get(`/kskresults/visits/${idBenhNhan}`);
    return data;
  },
};

// ========== ĐẶT KHÁM ==========
export const DatKhamService = {
  // Public — không cần token
  getDoctors: async () => {
    const { data } = await api.get('/datkham/doctors');
    return data;
  },
  getServices: async () => {
    const { data } = await api.get('/datkham/services');
    return data;
  },
  checkPhone: async (phone, date) => {
    const { data } = await api.get('/datkham/check-phone', { params: { phone, date } });
    return data;
  },
  create: async (payload) => {
    const { data } = await api.post('/datkham', payload);
    return data;
  },

  // Admin — cần token admin
  list: async ({ search = '', from = '', to = '', page = 1, pageSize = 20 } = {}) => {
    const { data } = await api.get('/datkham', { params: { search, from, to, page, pageSize } });
    return data;
  },
  remove: async (id) => {
    const { data } = await api.delete(`/datkham/${id}`);
    return data;
  },
};

// ========== CÔNG TY / KSK (luồng admin & HR) ==========
export const CongTyService = {
  // Danh mục công ty/khách hàng KSK — cần token admin
  list: async () => {
    const { data } = await api.get('/kskresults/congty');
    return data;
  },

  // Danh sách nhân viên KSK theo công ty (tuNgay/denNgay: 'yyyy-MM-dd', optional)
  employees: async (idKhachHang, tuNgay = '', denNgay = '') => {
    const params = {};
    if (tuNgay) params.tuNgay = tuNgay;
    if (denNgay) params.denNgay = denNgay;
    const { data } = await api.get(`/kskresults/congty/${idKhachHang}/nhanvien`, { params });
    return data;
  },
};

// ========== IMPORT KẾT QUẢ KSK TỪ EXCEL (admin) ==========
export const KskImportService = {
  checkSoLo: async (soLo) => {
    const { data } = await api.get('/kskimport/check-solo', { params: { soLo } });
    return data; // { exists }
  },
  validate: async (payload) => {
    const { data } = await api.post('/kskimport/validate', payload);
    return data; // KskImportResult
  },
  commit: async (payload) => {
    const { data } = await api.post('/kskimport/commit', payload);
    return data; // KskImportResult
  },
  getSoLo: async (idKhachHang) => {
    const { data } = await api.get('/kskimport/solo', { params: { idKhachHang } });
    return data; // [{ id, ma_solo, ten_solo, ... }]
  },
  addSoLo: async (maSolo, tenSolo, idKhachHang) => {
    const { data } = await api.post('/kskimport/solo', { MaSolo: maSolo, TenSolo: tenSolo, IdKhachHang: idKhachHang });
    return data; // { id }
  },
  addKhachHang: async (maKhachHang, tenKhachHang, maSoThue) => {
    const { data } = await api.post('/kskimport/khachhang', { MaKhachHang: maKhachHang, TenKhachHang: tenKhachHang, MaSoThue: maSoThue });
    return data; // { id, ma }
  },
  getColumns: async () => {
    const { data } = await api.get('/kskimport/columns');
    return data; // { structId, reportCode, rowBegin, details: [...] }
  },
  saveColumnDetail: async (detail) => {
    const { data } = await api.post('/kskimport/columns/detail', detail);
    return data; // { id }
  },
  saveColumnDetails: async (details) => {
    const { data } = await api.post('/kskimport/columns/details', details);
    return data; // { ok, count }
  },
  deleteColumnDetail: async (id) => {
    const { data } = await api.delete(`/kskimport/columns/detail/${id}`);
    return data;
  },
  saveColumnMaster: async (rowBegin) => {
    const { data } = await api.post('/kskimport/columns/master', { RowBegin: rowBegin });
    return data;
  },
};

// ========== TẠO PHIẾU KẾT QUẢ + GỬI SMS (admin — web hóa frmPrintMany_QueVo) ==========
export const KskPrintService = {
  danhSach: async (params) => {
    const { data } = await api.get('/kskprint/danhsach', { params });
    return data; // KskPrintRow[]
  },
  pdfUrl: async (maLuotKham) => {
    const response = await api.get(`/kskprint/pdf/${maLuotKham}`, { responseType: 'blob' });
    return window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
  },
  process: async (payload) => {
    const { data } = await api.post('/kskprint/process', payload);
    return data; // KskPrintProcessResult
  },
};

// ========== RESULTS (kết quả KSK theo TT32 — KskResultsController) ==========
// Luồng cũ /results (ResultsController) vẫn tồn tại phía backend nếu cần rollback.
// Định danh 'pub:<token>' → luồng public không cần đăng nhập (/kskresults/public/...)
const kskBase = (ma) =>
  typeof ma === 'string' && ma.startsWith('pub:')
    ? `/kskresults/public/${encodeURIComponent(ma.slice(4))}`
    : `/kskresults/${ma}`;

export const ResultService = {
  getResults: async (maLuotKham) => {
    const { data } = await api.get(kskBase(maLuotKham));
    return data;
  },

  // Sinh link chia sẻ không cần đăng nhập (cần JWT admin/HR); days: null = vĩnh viễn
  getShareLink: async (maLuotKham, days = null) => {
    const { data } = await api.get(`/kskresults/${maLuotKham}/share-link`, { params: days ? { days } : {} });
    return data; // { token, path }
  },

  downloadFile: async (maLuotKham, fileId, fileName) => {
    const response = await api.get(`${kskBase(maLuotKham)}/files/${fileId}`, {
      responseType: 'blob',
    });

    const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },

  /**
   * Fetch a file as a blob URL for in-browser viewing. Caller MUST call
   * URL.revokeObjectURL(url) when the viewer is closed.
   */
  fetchFileUrl: async (maLuotKham, fileId) => {
    const response = await api.get(`${kskBase(maLuotKham)}/files/${fileId}`, {
      responseType: 'blob',
    });
    return window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
  },
};

export default api;
